import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Child } from '../types/child';
import { trackLogin, trackLogout, setUserProperties } from '../utils/analytics';

export type UserRole = 'ENDUSER' | 'ADMIN' | 'SUPERADMIN';

export interface Subscription {
  type?: 'monthly' | 'yearly' | 'fouryear';
  status?: 'active' | 'expired';
  startDate?: string;
  endDate?: string;
  activatedAt?: string;
}

export interface User {
  _id?: string;
  id: string;
  email: string;
  name: string | null;
  avatar: string | null;
  role?: UserRole;
  children?: Child[];
  activeChildId?: string;
  privacyPolicyAccepted?: boolean;
  privacyPolicyAcceptedAt?: string;
  subscription?: Subscription;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  refetchUser: () => Promise<void>;
  needsOnboarding: boolean;
  activeChild: Child | null;
  setActiveChild: (childId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  logout: async () => {},
  refetchUser: async () => {},
  needsOnboarding: false,
  activeChild: null,
  setActiveChild: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async (isInitialLoad = false) => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data ?? null);
        
        // Set GA user properties when user is loaded
        if (data) {
          const activeChild = data.children?.find((c: Child) => c._id === data.activeChildId) ?? data.children?.[0];
          setUserProperties({
            user_id: data.id,
            is_premium: data.subscription?.status === 'active',
            child_count: data.children?.length ?? 0,
            child_age: activeChild?.ageInMonths ? Math.floor(activeChild.ageInMonths / 12) : undefined,
          });
          
          // Track login on initial page load if user already logged in
          // (actual login is tracked on callback page)
        }
        return data;
      }
      
      // Handle banned user (403)
      if (res.status === 403) {
        const data = await res.json();
        if (data.isBanned && window.location.pathname !== '/banned') {
          window.location.href = '/banned';
          return null;
        }
      }
      
      setUser(null);
      return null;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchUser().finally(() => setLoading(false));
  }, [fetchUser]);

  const logout = async () => {
    trackLogout();
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // ignore
    }
    setUser(null);
    window.location.reload();
  };

  const refetchUser = async () => {
    await fetchUser();
  };

  // User needs onboarding if logged in but has no children
  const needsOnboarding = !!user && (!user.children || user.children.length === 0);

  // Compute active child
  const activeChild = user?.children?.find(c => c._id === user.activeChildId) 
    ?? user?.children?.[0] 
    ?? null;

  const setActiveChild = async (childId: string) => {
    try {
      await fetch(`/api/children/active/${childId}`, {
        method: 'PUT',
        credentials: 'include',
      });
      await fetchUser();
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, refetchUser, needsOnboarding, activeChild, setActiveChild }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
